import React, { useContext, useEffect, useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Spinner from "react-bootstrap/Spinner";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import Button from "react-bootstrap/Button";
import Axios from "axios";
import { Store } from "../Store";
import Alert from "react-bootstrap/Alert";
import { toast } from "react-toastify";
import { getError } from "../utils";
import { api } from "../Api";
import { useOtpGate, OtpGateModal } from "../component/OtpGate";
import SecurityBadges from "../component/SecurityBadges";

// Where a withdraw request goes. "Agent" pays out real money to the
// member's saved bank/mobile-banking details, same as always. Any upline
// merchant behaves exactly the same as Agent — real money, paid by hand —
// except the merchant approves it themselves, not staff. "Self Topup" never
// leaves the platform — it credits the member's own Shopping Balance
// instead, and shows up in their Topup History once released.
const BASE_DESTINATIONS = [
  { key: "Agent", label: "Agent (paid to your bank/mobile banking)" },
];
const SELF_TOPUP_DESTINATION = {
  key: "Self Topup",
  label: "Self Topup (add to my Shopping Balance)",
};

export default function WithdrawScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const navigate = useNavigate();
  const [AvilablePoint, setAvilablePoint] = useState(0);
  const [convertFrom, setConvertFrom] = useState(0);
  const [convertTo, setConvertTo] = useState("Agent");
  const [uplineMerchants, setUplineMerchants] = useState([]);
  const isSelfTopup = convertTo === "Self Topup";
  const isMerchantDest = uplineMerchants.some((m) => m.username === convertTo);
  // Neither ever needs the member's bank/mobile-banking details — Self Topup
  // never leaves the platform, and a merchant pays the member directly.
  const skipBankDetails = isSelfTopup || isMerchantDest;
  const withdrawDestinations = [
    ...BASE_DESTINATIONS,
    ...uplineMerchants.map((m) => ({
      key: m.username,
      label: `${m.associationName || m.name} — Merchant (@${m.username})`,
      logo: m.associationLogo,
    })),
    SELF_TOPUP_DESTINATION,
  ];
  const [amount, setAmount] = useState(0);
  const [password, setPassword] = useState("");
  const [loading, setloading] = useState(0);
  const [withdrawCharge, setWithdrawCharge] = useState(0);
  // Money Controls. Defaults keep the screen working against an older backend
  // that does not send the block.
  const [policy, setPolicy] = useState({
    withdrawEnabled: true,
    withdrawDisabledMessage: "",
    minimumWithdraw: 0,
    maximumWithdraw: 0,
  });
  const grossAmount = Number(amount) || 0;
  const feeAmount = Math.round(((grossAmount * withdrawCharge) / 100) * 100) / 100;
  const grandTotal = Math.round((grossAmount - feeAmount) * 100) / 100;

  const [profile, setProfile] = useState();
  const [mobilebank, setmobilebank] = useState();
  const [mobilebankN, setmobilebankN] = useState();
  const [bank, setBank] = useState();
  const [bankN, setBankN] = useState();

  async function getUserSettings() {
    const { data } = await Axios.post(
      api + "/api/availablebalance/user",
      { id: userInfo._id },
      {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      },
    );
    // window.alert(data.mobilebankaccount);
    setmobilebank(data.mobilebankname);

    if (
      data.mobilebankname === null ||
      data.mobilebankaccount === null ||
      data.fatherphone === null ||
      data.motherphone === null ||
      data.nid === null ||
      data.spousephone === null ||
      data.benificiaryname === null ||
      data.benificiarynid === null ||
      data.benificiaryrelation === null ||
      data.profilePhoto ===
        "https://res.cloudinary.com/dddogyqqa/image/upload/v1670435815/logo/clickcart-logo_wfktlg.png" ||
      data.sign === "null"
    ) {
      setProfile(0);
    } else {
      setmobilebank(data.mobilebankname);
      setmobilebankN(data.mobilebankaccount);
      setBank(data.bankname);
      setBankN(data.bankaccount);
      setProfile(1);
    }
  }
  // Withdraw only pulls from Available Balance now — Group Sales, Profit
  // Incentive, and Income Generation used to be withdrawable directly too,
  // but Convert already turns all three into Available Balance, so this
  // screen no longer needs to fetch or display those pools.
  const fetchData = async () => {
    try {
      const { data } = await Axios.get(
        api + `/api/users/point/${userInfo._id}`,
        {
          headers: { authorization: `Bearer ${userInfo.token}` },
        },
      );
      setWithdrawCharge(data.withdrawCharge);
      if (data.moneyPolicy) setPolicy(data.moneyPolicy);
      setAvilablePoint(data.availablebalance);
    } catch (error) {
      // window.alert(error);
    }
  };

  const fetchUplineMerchants = async () => {
    try {
      const { data } = await Axios.get(
        api + "/api/association/upline",
        { headers: { authorization: `Bearer ${userInfo.token}` } },
      );
      setUplineMerchants(data.rows || []);
    } catch (error) {
      // informational — Agent/Self Topup still work without this
    }
  };

  useEffect(() => {
    let active = true;
    getUserSettings();
    fetchData();
    fetchUplineMerchants();
    return () => {
      active = false;
    };
  }, [userInfo]);

  const otpGate = useOtpGate({
    path: "withdraw",
    token: userInfo.token,
    onSuccess: (data) => {
      toast.info(data?.message || "Withdrawal request submitted.");
      fetchData();
      setAmount(0);
      setPassword("");
    },
    onError: (error) => {
      fetchData();
      setAmount(0);
      setPassword("");
      toast.error(getError(error));
    },
  });

  const convert = async () => {
    // Enforced server-side as well; this just avoids a pointless round trip.
    if (!policy.withdrawEnabled) {
      toast.error(
        policy.withdrawDisabledMessage ||
          "Withdrawals are temporarily turned off. Please try again later.",
      );
      return;
    }
    if (!skipBankDetails && (convertFrom === "0" || convertFrom === 0)) {
      toast.error("Please select a payment method.");
      return;
    }
    if (!convertTo || convertTo === "0") {
      toast.error("Please choose where this withdraw should go.");
      return;
    }
    if (!grossAmount || grossAmount <= 0) {
      toast.error("Please enter an amount greater than 0.");
      return;
    }
    if (!password || password.length < 4) {
      toast.error("Please enter your password (at least 6 characters).");
      return;
    }
    if (
      Number(policy.minimumWithdraw) > 0 &&
      grossAmount < Number(policy.minimumWithdraw)
    ) {
      toast.error("Minimum withdraw amount is " + policy.minimumWithdraw + ".");
      return;
    }
    if (
      Number(policy.maximumWithdraw) > 0 &&
      grossAmount > Number(policy.maximumWithdraw)
    ) {
      toast.error("Maximum withdraw amount is " + policy.maximumWithdraw + ".");
      return;
    }
    if (grossAmount > AvilablePoint) {
      toast.error(
        "Not enough Available Balance. You have " + AvilablePoint + ".",
      );
      return;
    }
    // Goes through the OTP gate. When the gate is off this is exactly the
    // request the screen always made; when it is on, the hook collects the
    // code and replays this body to /withdraw/confirm.
    setloading(1);
    await otpGate.start({
      withdrawSource: "availablebalance",
      paymentmethod: skipBankDetails ? undefined : convertFrom,
      username: userInfo.username,
      to: convertTo,
      amount: grandTotal,
      grossAmount,
      charge: feeAmount,
      password: password,
    });
    setloading(0);
  };

  function gotoHome() {
    navigate("/user-panel");
  }
  return (
    <div>
      <Helmet>
        <title>Withdraw </title>
      </Helmet>
      <OtpGateModal
        gate={otpGate}
        title="Confirm your withdrawal"
        actionLabel="Confirm withdrawal"
      />
      <Container className="no-gutter" style={{}}>
        <div className="card">
          <Row>
            <Col style={{ textAlign: "center" }}>
              <img
                src="https://res.cloudinary.com/dddogyqqa/image/upload/v1670435815/logo/clickcart-logo_wfktlg.png"
                style={{ width: "15rem", margin: "2rem" }}
                alt="ClickCart Logo"
              />
            </Col>
          </Row>
          <div
            className="card-header"
            onClick={() => {
              gotoHome();
            }}
          >
            <i className="fa fa-long-arrow-left" aria-hidden="true"></i> {"  "}{" "}
            Withdraw
          </div>

          <div className="card-body w-90" style={{ margin: "auto" }}>
            {!policy.withdrawEnabled && (
              <Alert key="withdraw-off" variant="danger">
                {policy.withdrawDisabledMessage ||
                  "Withdrawals are temporarily turned off. Please try again later."}
              </Alert>
            )}
            {policy.withdrawEnabled &&
              (Number(policy.minimumWithdraw) > 0 ||
                Number(policy.maximumWithdraw) > 0) && (
                <Alert key="withdraw-limits" variant="info" className="py-2">
                  <span className="small">
                    {Number(policy.minimumWithdraw) > 0 &&
                      `Minimum withdraw: ${policy.minimumWithdraw}. `}
                    {Number(policy.maximumWithdraw) > 0 &&
                      `Maximum withdraw: ${policy.maximumWithdraw}.`}
                  </span>
                </Alert>
              )}
            {profile ? (
              <Alert key="info" variant="info">
                Your Profile is Updated and ready for withdraw. Please check
                <Alert.Link href="/settings#/settings"> Settings</Alert.Link> &
                check Your Bank Information.
              </Alert>
            ) : skipBankDetails ? (
              <Alert key="self-topup-ok" variant="info">
                {isMerchantDest
                  ? "This merchant pays you directly — no bank/mobile-banking details needed."
                  : "Self Topup doesn't need your bank/mobile-banking details — it credits your own Shopping Balance instead."}
              </Alert>
            ) : (
              <Alert key="danger" variant="danger">
                Please Complete Your Profile Before Withdraw. Go To
                <Alert.Link href="/settings#/settings"> Settings </Alert.Link> &
                Update Your Profile.
              </Alert>
            )}
            <span>
              Available Balance: {AvilablePoint.toFixed(2)} | Withdraw Charge:{" "}
              {withdrawCharge}%
            </span>
            <div className="input-group mb-3 mt-2">
              <div className="input-group-text">
                <i className="fa fa-share-square" aria-hidden="true"></i>
              </div>
              <select
                className="form-control"
                required
                value={convertTo}
                onChange={(e) => setConvertTo(e.target.value)}
              >
                {withdrawDestinations.map((d) => (
                  <option value={d.key} key={d.key}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
            {isSelfTopup && (
              <div className="form-text mb-3">
                Self Topup never leaves ClickCart — the net points are added
                to your Shopping Balance at 1 point = Tk 12, and show up in
                your Topup History.
              </div>
            )}
            {isMerchantDest && (
              <div className="form-text mb-3">
                No bank/mobile-banking details needed — the merchant pays you
                directly and approves this from their own panel.
              </div>
            )}
            {!skipBankDetails && (
              <div className="input-group mb-3">
                <div className="input-group-text">
                  <i className="fa fa-level-down" aria-hidden="true"></i>
                </div>
                <select
                  className="form-control "
                  required
                  onChange={(e) => setConvertFrom(e.target.value)}
                >
                  <option value="0">Select Payment Method</option>
                  <option value="bkash">
                    {mobilebank
                      ? mobilebank + " - " + mobilebankN
                      : "No Mobile Banking Information"}
                  </option>
                  <option value="bank">
                    {bank ? bank + " - " + bankN : "No Bank Information"}
                  </option>
                </select>{" "}
              </div>
            )}
            <div className="mb-3">
              <div className="input-group">
                <div className="input-group-text">
                  <i className="fa fa-dollar" aria-hidden="true"></i>
                </div>
                <input
                  className="form-control"
                  type="number"
                  placeholder="Amount"
                  required
                  disabled={loading || (!profile && !skipBankDetails) ? true : false}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                ></input>
              </div>
              {grossAmount > 0 ? (
                <span className="small">
                  Charge: {feeAmount} | Net Payable: {grandTotal}
                  {isSelfTopup && (
                    <>
                      {" "}| Shopping Balance credit: Tk{" "}
                      {(Math.round(Number(grandTotal || 0) * 12 * 100) / 100).toLocaleString()}
                    </>
                  )}
                </span>
              ) : (
                <span className="small"></span>
              )}{" "}
            </div>
            <div className="input-group mb-3">
              <div className="input-group-text">
                <i className="fa fa-asterisk" aria-hidden="true"></i>
              </div>
              <input
                type="password"
                className="form-control"
                placeholder="Password"
                required
                disabled={loading || (!profile && !skipBankDetails) ? true : false}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <Button
              variant="outline-success w-100"
              className={
                loading ||
                (!profile && !skipBankDetails) ||
                !policy.withdrawEnabled
                  ? "disabled"
                  : ""
              }
              disabled={
                Boolean(loading) ||
                (!profile && !skipBankDetails) ||
                !policy.withdrawEnabled
              }
              onClick={convert}
            >
              {loading ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                ""
              )}{" "}
              Submit
            </Button>
            <SecurityBadges variant="inline" />
          </div>

          <div style={{ height: "51vh" }}> {""}</div>
        </div>
      </Container>
    </div>
  );
}
